import { useApolloClient } from '@apollo/client';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { DataGrid, GridCallbackDetails, GridColDef, GridRenderCellParams, GridSelectionModel } from '@mui/x-data-grid';
import { appRoutes } from 'app/app-routes';
import ConfirmDialog, { ConfirmDialogProps } from 'common/components/ConfirmDialog';
import { imageHelper } from 'common/functions/image-helper';
import { formatters } from 'common/tools/formatters';
import { ListingStatus, ListingStatusValues } from 'common/types/listing-status';
import { LISTINGS_BY_USER_QUERY, LISTINGS_SEARCH_QUERY } from 'data/ListingsSearchQuery.graphql';
import {
  ListingImage,
  ListingsFindByUserQuery,
  ListingsSearchQuery,
  useListingsDeleteMutation,
} from 'graphql/generated';
import sellLogo from 'images/sell2.png';
import _ from 'lodash';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useNavigate } from 'react-router-dom';
import { cacheActions, cacheSelectors } from 'redux/cache.slice';
import { newListingActions, newListingSelectors } from 'redux/new-listing.slice';
import { RootState } from 'redux/root-reducer';
import WsButton from 'stories/inputs/WsButton';
import WsFlexbox from 'stories/layout/WsFlexbox';
import WsInfoFrame from 'stories/layout/WsInfoFrame';

interface UserListing {
  id: string;
  heading: string;
  images: ListingImage[];
  listingDate: string;
  price: string;
  status: string;
}

const columns: GridColDef[] = [
  { field: 'id', headerName: 'ID', width: 90 },
  {
    field: 'images',
    headerName: 'Image',
    width: 150,
    editable: false,
    sortable: false,
    renderCell: (params: GridRenderCellParams<ListingImage[]>) => (
      <>
        {params.value.length ? (
          <img
            style={{ width: '100%', height: 'auto' }}
            alt="car"
            src={imageHelper.getPrimaryImageLocation(params.value) as string}
          />
        ) : (
          <WsFlexbox centered sx={{ backgroundColor: '#EEE', width: '100%', height: '100%' }}>
            <Typography variant="subtitle2" color="primary">
              Unpublished
            </Typography>
          </WsFlexbox>
        )}
      </>
    ),
  },
  {
    field: 'heading',
    headerName: 'Heading',
    width: 150,
    editable: false,
  },
  {
    field: 'price',
    headerName: 'Price',
    width: 90,
    editable: false,
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 90,
    editable: false,
  },
  {
    field: 'listingDate',
    headerName: 'Listing Date',
    width: 120,
    editable: false,
  },
  {
    field: 'expireDate',
    headerName: 'Expiration Date',
    width: 120,
    editable: false,
  },
];

const Sell = () => {
  const dispatch = useDispatch();
  const client = useApolloClient();
  const [deleteListing] = useListingsDeleteMutation();
  const navigate = useNavigate();

  const [runEffect, runEffectTo] = useState(true);
  const [listings, listingsTo] = useState<UserListing[]>([]);
  const [selectionModel, selectionModelTo] = useState<GridSelectionModel>([]);
  const [showContinueOrNewDlg, showContinueOrNewDlgTo] = useState(false);
  const [showDeleteDialog, showDeleteDialogTo] = useState(false);
  const id = useSelector((state: RootState) => newListingSelectors.id(state));
  const heading = useSelector((state: RootState) => newListingSelectors.heading(state));
  const price = useSelector((state: RootState) => newListingSelectors.price(state));
  const hasCachedData = useSelector((state: RootState) => newListingSelectors.hasCachedData(state));
  const user = useSelector((state: RootState) => cacheSelectors.user(state));

  useEffect(() => {
    if (runEffect) {
      if (hasCachedData) {
        dispatch(newListingActions.popCache());
        return;
      } else if (id.length) {
        dispatch(newListingActions.init());
        return;
      }
      runEffectTo(false);
      const findUserListings = async (userId: string) => {
        const { data } = await client.query<ListingsFindByUserQuery>({
          query: LISTINGS_BY_USER_QUERY,
          variables: { filters: [{ field: 'userId', strVal: userId }] },
        });
        // console.log(data?.listingsFind.length);
        if (data?.listingsFind) {
          listingsTo([
            ...data.listingsFind.map(x => ({
              id: x.id,
              heading: x.heading,
              price: formatters.currency.format(x.price),
              listingDate: moment(x.listingDate).format('MMM DD, YYYY'),
              expireDate: moment(x.expireDate).format('MMM DD, YYYY'),
              status: ListingStatusValues[x.status as ListingStatus],
              images: x.images,
            })),
            ...(heading
              ? [
                  {
                    id: id,
                    heading: heading,
                    price: formatters.currency.format(price),
                    listingDate: 'Not Published',
                    expireDate: '',
                    status: 'New',
                    images: [],
                  },
                ]
              : []),
          ]);
        }
      };
      if (user) findUserListings(user.id);
    }
  }, [dispatch, client, user, id, heading, price, runEffect, listings, hasCachedData]);

  //#region Create new
  const onConfirmCreateNew = () => {
    showContinueOrNewDlgTo(false);
    dispatch(newListingActions.createNew(user!.email));
    navigate(appRoutes.createListing);
  };

  const onConfirmContinueEditing = () => {
    showContinueOrNewDlgTo(false);
    navigate(appRoutes.createListing);
  };

  const onClickCreateNew = () => {
    if (heading) showContinueOrNewDlgTo(true);
    else {
      dispatch(newListingActions.createNew(user!.email));
      navigate(appRoutes.createListing);
    }
  };

  const createNewDialogProps: ConfirmDialogProps = {
    open: showContinueOrNewDlg,
    title: 'Unpublished Listing Found',
    content: `Continue editing or create new? If you select 'Create New', your unpublished changes will be lost.`,
    ackLabel: 'Create New',
    ackAction: onConfirmCreateNew,
    nackLabel: 'Continue Editing',
    nackAction: onConfirmContinueEditing,
  };
  //#endregion Create N

  //#region Delete
  const onConfirmDelete = async () => {
    try {
      showDeleteDialogTo(false);

      if (selectionModel[0] === '') throw new Error('Should not be here');
      else {
        await deleteListing({
          variables: { listingsDeleteId: selectionModel[0] as string },
        });
        await client.clearStore();
      }
      runEffectTo(true);
      dispatch(
        cacheActions.addSnack({
          message: 'Listing Deleted',
          variant: 'success',
        }),
      );
    } catch (error: any) {
      dispatch(
        cacheActions.addSnack({
          message: error.message,
          variant: 'error',
        }),
      );
    }
  };

  const onConfirmDeleteCancel = () => {
    showDeleteDialogTo(false);
  };

  const onClickDelete = () => {
    if (selectionModel[0] === '') {
      dispatch(newListingActions.init());
      runEffectTo(true);
    } else showDeleteDialogTo(true);
  };

  const deleteDialogProps: ConfirmDialogProps = {
    open: showDeleteDialog,
    title: 'Delete Listing',
    content: 'Are you sure you want to delete this listing?',
    ackLabel: 'Delete',
    ackAction: onConfirmDelete,
    nackLabel: 'Cancel',
    nackAction: onConfirmDeleteCancel,
  };
  //#endregion Delete

  const onEditClick = async () => {
    try {
      if (selectionModel[0] !== '' && listings.find(x => x.id === '')) {
        console.log('we have a temp listing');
        dispatch(newListingActions.pushCache());
      }
      if (selectionModel[0] !== '') {
        const { data } = await client.query<ListingsSearchQuery>({
          query: LISTINGS_SEARCH_QUERY,
          variables: { filters: [{ field: 'id', strVal: selectionModel[0] }] },
        });
        if (data?.listingsSearch) {
          const listing = data.listingsSearch.listings[0];
          // console.log('listing find', data.listingsSearch.listings[0]);
          dispatch(newListingActions.loadListing(listing));
        } else throw new Error('Error loading listing');
      }
      navigate(appRoutes.createListing);
    } catch (error: any) {
      dispatch(
        cacheActions.addSnack({
          message: error.message,
          variant: 'error',
        }),
      );
    }
  };

  const handleDetailsClick = () => {
    // console.log('handle details', selectionModel[0]);
    navigate(`${appRoutes.itemDetail}/${selectionModel[0]}/${encodeURIComponent('My Listings')}`);
    // navigate(`${appRoutes.listingInfo}/${selectionModel[0]}`);
  };

  const onSelectChange = async (selections: GridSelectionModel, details: GridCallbackDetails) => {
    // console.log('selection model', selections);
    if (selections.length > 1) {
      const selectionSet = new Set(selectionModel);
      const result = selections.filter(s => !selectionSet.has(s));

      selectionModelTo(result);
    } else {
      selectionModelTo(selections);
    }
  };

  const onLoginClick = () => {
    dispatch(cacheActions.loginNavigate(true));
    navigate(appRoutes.login);
  };

  return (
    <Grid container justifyContent="center" sx={{ flexGrow: 1 }}>
      <ConfirmDialog {...createNewDialogProps} />
      <ConfirmDialog {...deleteDialogProps} />
      <Grid container sx={{ width: '90%' }}>
        <Grid item xs={12}>
          <Typography variant="h4" color="primary">
            Sell Your Car
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <img src={sellLogo} alt="sell" width="100%" />
        </Grid>
        <Grid container item xs={12}>
          <Grid container justifyContent="center">
            {user?.id ? (
              <>
                <WsInfoFrame width="80%" title="My Listings" icon={<DirectionsCarIcon color="secondary" />}>
                  <Grid container>
                    <Grid item xs={12}>
                      <WsFlexbox gap={1}>
                        <WsButton
                          variant="outlined"
                          label="View Listing"
                          size="small"
                          color="primary"
                          disabled={selectionModel.length !== 1 || selectionModel[0] === ''}
                          onClick={() => handleDetailsClick()}
                        />
                        <WsButton
                          variant="outlined"
                          label="Edit"
                          size="small"
                          color="primary"
                          disabled={selectionModel.length !== 1}
                          onClick={() => onEditClick()}
                        />
                        <WsButton
                          label="Delete"
                          variant="outlined"
                          size="small"
                          color="primary"
                          disabled={!selectionModel.length}
                          onClick={() => onClickDelete()}
                        />
                        <Box sx={{ flexGrow: 1 }}></Box>
                        <Box>
                          <WsButton
                            label="Create New"
                            size="small"
                            color="secondary"
                            sx={{ flexGrow: 1 }}
                            onClick={() => onClickCreateNew()}
                          />
                        </Box>
                      </WsFlexbox>
                    </Grid>
                  </Grid>
                  <Box sx={{ height: 372, width: '100%' }}>
                    <DataGrid
                      sx={{
                        columnHeaderCheckbox: {
                          color: 'red',
                        },
                        '& .MuiDataGrid-columnHeaderTitleContainer .MuiCheckbox-root': {
                          color: 'red',
                          display: 'none',
                        },
                      }}
                      rows={listings}
                      columns={columns}
                      pageSize={5}
                      rowsPerPageOptions={[5]}
                      columnVisibilityModel={{ id: false }}
                      onSelectionModelChange={onSelectChange}
                      editMode="row"
                      disableColumnFilter
                      checkboxSelection
                      disableColumnMenu
                      disableColumnSelector
                      disableDensitySelector
                      selectionModel={selectionModel}
                    />
                  </Box>
                </WsInfoFrame>
              </>
            ) : (
              <>
                <Grid item xs={12}>
                  <WsFlexbox centered sx={{ paddingTop: 2 }}>
                    <Typography variant="h6">To create and manage listings, sign in to your account.</Typography>
                  </WsFlexbox>
                </Grid>
                <Grid item xs={12}>
                  <WsFlexbox centered>
                    <WsButton label="Log In" color="primary" onClick={() => onLoginClick()} />
                  </WsFlexbox>
                </Grid>
                <Grid item xs={12}>
                  <WsFlexbox centered>
                    <NavLink to={appRoutes.register}>Create an account</NavLink>
                  </WsFlexbox>
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default Sell;
